/**
 * Organization Tokens Store Slice
 *
 * Zustand slice for organization-level token management.
 * Handles balance and transactions for org admins.
 * Related to Beads issue gw-spa-9hs
 */

import {
  clearError as clearOpError,
  hasOperationLoaded,
  isOperationLoading,
  setError,
  setLoaded,
  setLoading,
} from '@gw/ui';

import { paymentsApi } from '@/api/payments';

import type {
  ErrorStates,
  ErrorStatuses,
  LoadedTimestamps,
  LoadingStates,
  GiftCardRedemptionResponse,
  OrganizationSubscription,
  TokenBalance,
  TokenTransaction,
  TransactionPagination,
} from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface OrganizationTokensSlice {
  // State properties
  organizationBalance: TokenBalance | null;
  organizationTransactions: TokenTransaction[];
  organizationTransactionPagination: TransactionPagination | null;
  organizationSubscription: OrganizationSubscription | null;
  isLoading: LoadingStates;
  hasLoaded: LoadingStates;
  lastLoaded: LoadedTimestamps;
  errors: ErrorStates;
  errorStatuses: ErrorStatuses;

  // Organization balance actions
  fetchOrganizationBalance: (orgId: string) => Promise<void>;

  // Organization transaction history actions
  fetchOrganizationTransactions: (orgId: string, limit?: number) => Promise<void>;

  // Organization purchase actions
  purchaseOrganizationTokens: (
    orgId: string,
    packageId: string,
    type: 'one_time'
  ) => Promise<string>; // Returns checkout URL

  purchaseCustomTokens: (orgId: string, amount: number) => Promise<string>; // Returns checkout URL

  // Gift card redemption for organization
  redeemGiftCardForOrg: (orgId: string, code: string) => Promise<GiftCardRedemptionResponse>;

  // Token purchase modal state
  showTokenPurchaseModal: boolean;
  openTokenPurchaseModal: () => void;
  closeTokenPurchaseModal: () => void;

  // Utility actions
  clearError: (operation?: string) => void;
  checkIsLoading: (operation: string) => boolean;
  checkHasLoaded: (operation: string) => boolean;
}

// ============================================================================
// Slice Creator
// ============================================================================

// GW-8532: In-flight request dedup for fetchOrganizationBalance. Several
// independent mount effects call this action for the same orgId within the
// same render pass — ProtectedRoute (app-level guard), HeaderHost (global
// header, mounted once but re-fires on org switch), plus per-page effects
// (OrganizationTransactionsPage, OrganizationSummaryPage, TokensPage,
// GiftCardsPage, CreateGiftCardModal, RFICreatePage) — none of which know
// about each other. Rather than track down and coordinate every call site,
// dedupe at the source: concurrent calls for the same orgId share one
// in-flight network request instead of firing 3-5 identical GETs
// (org-audit GW-8532). Kept as a closure variable (not slice state) so it
// never triggers a re-render and is not persisted.
let inFlightBalanceFetch: { orgId: string; promise: Promise<void> } | null = null;

export const createOrganizationTokensSlice: StateCreator<OrganizationTokensSlice> = (set, get) => ({
  // State initialization
  organizationBalance: null,
  organizationTransactions: [],
  organizationTransactionPagination: null,
  organizationSubscription: null,
  isLoading: {},
  hasLoaded: {},
  lastLoaded: {},
  errors: {},
  errorStatuses: {},

  // Token purchase modal initial state
  showTokenPurchaseModal: false,
  openTokenPurchaseModal: () => set({ showTokenPurchaseModal: true }),
  closeTokenPurchaseModal: () => set({ showTokenPurchaseModal: false }),

  // Fetch organization token balance
  // GW-6534: Stale-fetch protection. When the user switches orgs mid-fetch,
  // the resolution from the previous org must NOT write balance for the new
  // org. We re-check the store's `currentOrg.id` at write time and drop the
  // result if it has changed. The `paymentsApi` boundary already validates
  // orgId via assertOrgId — so a malformed value never reaches the network.
  //
  // GW-8532: In-flight dedup. Multiple independent mount effects (see
  // inFlightBalanceFetch doc comment above) call this for the same orgId in
  // the same render pass. If a fetch for `orgId` is already in flight,
  // every caller awaits that single promise instead of issuing a new GET.
  fetchOrganizationBalance: (orgId: string): Promise<void> => {
    if (inFlightBalanceFetch && inFlightBalanceFetch.orgId === orgId) {
      return inFlightBalanceFetch.promise;
    }

    const promise = (async () => {
      const operation = 'fetchOrganizationBalance';
      const state = get();
      set({ isLoading: setLoading(state.isLoading, operation, true) });

      try {
        const balance = await paymentsApi.getOrganizationTokenBalance(orgId);

        // GW-6534: After the fetch resolves, verify that `currentOrg.id` is
        // still the orgId we fetched for. If the user switched orgs mid-flight,
        // drop the result rather than overwriting the new org's balance.
        const liveState = get() as unknown as {
          currentOrg?: { id?: string } | null;
          hasLoaded: typeof state.hasLoaded;
          lastLoaded: typeof state.lastLoaded;
          isLoading: typeof state.isLoading;
          errors: typeof state.errors;
        };
        const liveOrgId = liveState.currentOrg?.id ?? null;
        if (liveOrgId !== null && liveOrgId !== orgId) {
          // Stale fetch — clear our loading flag but do not write balance.
          set({
            isLoading: setLoading(liveState.isLoading, operation, false),
          });
          return;
        }

        const loadedState = setLoaded(liveState.hasLoaded, liveState.lastLoaded, operation, true);
        set({
          organizationBalance: balance,
          isLoading: setLoading(liveState.isLoading, operation, false),
          hasLoaded: loadedState.hasLoaded,
          lastLoaded: loadedState.lastLoaded,
          errors: clearOpError(liveState.errors, operation),
        });
      } catch (error) {
        const currentState = get();
        set({
          isLoading: setLoading(currentState.isLoading, operation, false),
          errors: setError(
            currentState.errors,
            operation,
            error instanceof Error ? error.message : String(error)
          ),
        });
        // GW-6428: Do not rethrow. Callers read error state from the store and
        // 8/12 callers already use .catch(() => {}) expecting the slice to swallow.
        // Rethrowing produced unhandled rejections in Sentry on every 4xx/5xx.
      } finally {
        // GW-8532: Clear the in-flight marker only if it still points at
        // this fetch (a newer fetch for a different orgId may have already
        // replaced it).
        if (inFlightBalanceFetch?.orgId === orgId) {
          inFlightBalanceFetch = null;
        }
      }
    })();

    inFlightBalanceFetch = { orgId, promise };
    return promise;
  },

  // Fetch organization transaction history
  fetchOrganizationTransactions: async (orgId: string, limit?: number) => {
    const operation = 'fetchOrganizationTransactions';
    const state = get();
    set({ isLoading: setLoading(state.isLoading, operation, true) });

    try {
      const response = await paymentsApi.getOrganizationTransactions(orgId, { limit });
      const currentState = get();
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        true
      );
      set({
        organizationTransactions: response.transactions,
        organizationTransactionPagination: response.pagination,
        isLoading: setLoading(currentState.isLoading, operation, false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        errors: clearOpError(currentState.errors, operation),
      });
    } catch (error) {
      const currentState = get();
      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        errors: setError(
          currentState.errors,
          operation,
          error instanceof Error ? error.message : String(error)
        ),
      });
      throw error;
    }
  },

  // Purchase organization tokens (create Stripe checkout session)
  purchaseOrganizationTokens: async (orgId: string, packageId: string, type: 'one_time') => {
    const operation = 'purchaseOrganizationTokens';
    const state = get();
    set({ isLoading: setLoading(state.isLoading, operation, true) });

    try {
      const currentUrl = window.location.origin;
      const response = await paymentsApi.createOrganizationCheckout({
        organizationId: orgId,
        packageId,
        type,
        successUrl: `${currentUrl}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${currentUrl}/payment/cancel`,
      });

      const currentState = get();
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        true
      );
      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        errors: clearOpError(currentState.errors, operation),
      });

      // Return checkout URL for redirect
      return response.url;
    } catch (error) {
      const currentState = get();
      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        errors: setError(
          currentState.errors,
          operation,
          error instanceof Error ? error.message : String(error)
        ),
      });
      throw error;
    }
  },

  // Purchase custom amount of tokens (create Stripe checkout session)
  purchaseCustomTokens: async (orgId: string, amount: number) => {
    const operation = 'purchaseCustomTokens';
    const state = get();
    set({ isLoading: setLoading(state.isLoading, operation, true) });

    try {
      const currentUrl = window.location.origin;
      const response = await paymentsApi.createOrganizationCheckout({
        organizationId: orgId,
        customAmount: amount,
        type: 'one_time',
        successUrl: `${currentUrl}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${currentUrl}/payment/cancel`,
      });

      const currentState = get();
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        true
      );
      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        errors: clearOpError(currentState.errors, operation),
      });

      // Return checkout URL for redirect
      return response.url;
    } catch (error) {
      const currentState = get();
      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        errors: setError(
          currentState.errors,
          operation,
          error instanceof Error ? error.message : String(error)
        ),
      });
      throw error;
    }
  },

  // Redeem gift card for organization
  redeemGiftCardForOrg: async (orgId: string, code: string) => {
    const operation = 'redeemGiftCardForOrg';
    const state = get();
    set({ isLoading: setLoading(state.isLoading, operation, true) });

    try {
      const result = await paymentsApi.redeemGiftCard(orgId, code);

      const currentState = get();
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        true
      );

      // Redemption adds credits, increasing both available and total.
      // Re-fetch the full balance from the API to get a consistent
      // { available, reserved, total } snapshot rather than partially
      // patching the cached value with only the new available amount.
      const freshBalance = await paymentsApi.getOrganizationTokenBalance(orgId);

      set({
        organizationBalance: freshBalance,
        isLoading: setLoading(currentState.isLoading, operation, false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        errors: clearOpError(currentState.errors, operation),
      });

      return result;
    } catch (error) {
      const currentState = get();
      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        errors: setError(
          currentState.errors,
          operation,
          error instanceof Error ? error.message : String(error)
        ),
      });
      throw error;
    }
  },

  // Utility actions
  clearError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({ errors: clearOpError(currentState.errors, operation) });
    } else {
      set({ errors: {} });
    }
  },
  checkIsLoading: (operation: string) => isOperationLoading(get().isLoading, operation),
  checkHasLoaded: (operation: string) => hasOperationLoaded(get().hasLoaded, operation),
});
